/**
 * Stripe integration helpers — browser-side only.
 *
 * These functions call the Supabase Edge Functions to create Stripe sessions,
 * then redirect the user's browser to the returned Stripe-hosted URL.
 *
 * All sensitive Stripe logic (API keys, webhook verification, DB writes)
 * lives in the Edge Functions. This module only handles the redirect handoff.
 */
import { supabase } from './supabase'

/**
 * Initiates a Stripe Checkout flow for a subscription upgrade.
 * The Edge Function creates a Stripe customer (if needed) and returns a
 * hosted checkout URL. The user is redirected there to complete payment.
 * On success, Stripe redirects to /profile?upgraded=1.
 *
 * @param tier       Which subscription: 'growth' (Practice) | 'premium' (Awakening)
 * @param interval   Billing frequency: 'month' | 'year'
 * @param isFounding Use the Founding Circle price (annual-only, web only, $59/$119, locked three years)
 */
export async function redirectToCheckout(
  tier: 'growth' | 'premium',
  interval: 'month' | 'year' = 'month',
  isFounding = false
): Promise<{ error?: string; code?: string }> {
  if (!supabase) return { error: 'Supabase not configured' }

  const { data, error } = await supabase.functions.invoke('create-checkout-session', {
    body: { tier, interval, isFounding },
  })

  if (error || !data?.url) {
    // Same unwrapping as redirectToPortal below: on a non-2xx, error.message is
    // the generic "Edge Function returned a non-2xx status code" and the
    // function's own { error, code } is in error.context. `code` is what a
    // caller branches on — 'founding_full' when every founding seat is taken
    // (#34), so the buyer can be told the Circle is full.
    let msg = 'Could not start checkout. Please try again.'
    let code: string | undefined
    const body = error
      ? await (error as { context?: Response }).context?.json?.().catch(() => null)
      : (data as { error?: string; code?: string } | null)
    if (body?.error) msg = body.error
    else if (error?.message && !error.message.includes('non-2xx')) msg = error.message
    if (typeof body?.code === 'string') code = body.code
    console.error('[stripe] create-checkout-session failed:', msg, { code })
    return { error: msg, ...(code ? { code } : {}) }
  }

  // Full page redirect — user leaves the app and goes to Stripe's hosted checkout
  window.location.href = data.url
  return {}
}

/**
 * Opens the Stripe Billing Portal for the current user.
 * Lets them update payment methods, view invoices, or cancel their subscription.
 * Only available for users who have previously checked out (have a stripe_customer_id).
 * On close, Stripe returns the user to /profile.
 */
export async function redirectToPortal(): Promise<{ error?: string }> {
  if (!supabase) return { error: 'Supabase not configured' }

  const { data, error } = await supabase.functions.invoke('create-portal-session', {})

  if (error || !data?.url) {
    // supabase.functions.invoke wraps non-2xx responses as FunctionsHttpError where
    // error.message is the generic "Edge Function returned a non-2xx status code".
    // The actual error from the Edge Function body lives in error.context (the Response).
    // Read it so Stripe's real error message (e.g. "No billing account found") surfaces.
    let msg = 'Could not open billing portal. Please try again.'
    if (error) {
      try {
        const body = await (error as { context?: Response }).context?.json?.()
        if (body?.error) msg = body.error
        else if (error.message && !error.message.includes('non-2xx')) msg = error.message
      } catch {
        if (error.message && !error.message.includes('non-2xx')) msg = error.message
      }
    }
    return { error: msg }
  }

  window.location.href = data.url
  return {}
}
